import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { buildSubdomain, buildSubdomainHost } from '@/lib/domain';

interface SiteModule {
  to: string;
  icon: string;
  title: string;
  description: string;
  accent: string;
}

const MODULES: SiteModule[] = [
  {
    to: '/dashboard/store',
    icon: 'ri-store-2-line',
    title: 'Ma boutique',
    description: "Apparence, thème, vitrine et réglages généraux de votre boutique en ligne.",
    accent: 'bg-primary-100 text-primary-700',
  },
  {
    to: '/dashboard/site-content',
    icon: 'ri-edit-line',
    title: 'Contenu du site',
    description: 'Textes et informations affichés sur les pages de votre site public.',
    accent: 'bg-accent-100 text-accent-700',
  },
  {
    to: '/dashboard/home-blocks',
    icon: 'ri-layout-masonry-line',
    title: "Blocs d'accueil",
    description: "Activez, masquez et réordonnez les sections de votre page d'accueil.",
    accent: 'bg-secondary-100 text-secondary-700',
  },
  {
    to: '/dashboard/banners',
    icon: 'ri-gallery-view-2',
    title: "Bannières d'accueil",
    description: 'Gérez les images et le diaporama affichés en haut de votre site.',
    accent: 'bg-primary-100 text-primary-700',
  },
  {
    to: '/dashboard/custom-pages',
    icon: 'ri-pages-line',
    title: 'Pages personnalisées',
    description: 'Créez et publiez des pages additionnelles avec leurs propres liens.',
    accent: 'bg-accent-100 text-accent-700',
  },
];

export default function MonSitewebPage() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const shopUrl = user ? buildSubdomain(user.user_name) : '';
  const shopHost = user ? buildSubdomainHost(user.user_name) : '';

  const copy = () => {
    if (!shopUrl) return;
    navigator.clipboard
      .writeText(shopUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-global-line text-primary-500"></i>
          Mon siteweb
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Tout ce qui concerne votre site public, regroupé au même endroit.
        </p>
      </div>

      {/* Lien du site */}
      {user && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-link-m text-primary-500"></i>
            <h3 className="text-sm font-semibold text-foreground-700">Le lien de votre site</h3>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <a
              href={shopUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex-1 flex items-center gap-2 min-w-0 px-4 py-3 rounded-lg bg-background-100 text-sm text-foreground-900 font-medium hover:text-primary-600 transition-colors"
            >
              <i className="ri-external-link-line text-foreground-400 flex-shrink-0"></i>
              <span className="truncate">{shopHost}</span>
            </a>
            <button
              onClick={copy}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
              {copied ? 'Lien copié' : 'Copier le lien'}
            </button>
          </div>
        </div>
      )}

      {/* Modules */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {MODULES.map((m) => (
          <Link
            key={m.to}
            to={m.to}
            className="group bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300 transition-colors cursor-pointer flex flex-col"
          >
            <div className={`w-11 h-11 rounded-lg ${m.accent} flex items-center justify-center mb-4`}>
              <i className={`${m.icon} text-xl`}></i>
            </div>
            <h3 className="text-base font-semibold text-foreground-900 mb-1.5">{m.title}</h3>
            <p className="text-sm text-foreground-500 leading-relaxed flex-1">{m.description}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
              Ouvrir
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}